import {
  IAccountRateUpdateReqSchema,
  IPriceReqSchema,
  IQuoteRespSchema,
} from '@monetize/types/app';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { apiGet, apiPut } from './axios';
import { cpqServiceQueryKeys } from './cpqService';
import { quoteOfferingRateServiceQueryKeys } from './queryKeysService';
import { enqueueForQuote } from './quoteWriteChain';

export interface QuoteOfferingRateEditability {
  rateId: string;
  /** True when the rate is an account-specific copy rather than a shared catalog rate. */
  accountSpecificRate: boolean;
  /**
   * True only when the rate is account-specific and nothing else depends on it
   * (not used on any other quote, subscription, or charge). When false, the rate
   * must be cloned instead of edited.
   */
  editable: boolean;
  /**
   * True when the same rate is applied to other offerings on this quote, so an
   * in-place edit will also change those offerings.
   */
  usedByOtherOfferingsOnThisQuote: boolean;
}

export interface QuoteOfferingRateEditReq {
  name: string;
  prices: IPriceReqSchema[];
  internalDescription?: string | null;
  /** Only set on percent-of-total rates; carries the editable min/max totals. */
  percentOfTotalConfig?: IAccountRateUpdateReqSchema['percentOfTotalConfig'];
}

export const useGetQuoteOfferingRateEditability = (
  quoteId: string,
  quoteOfferingId: string,
  rateId: string,
  options: Partial<UseQueryOptions<QuoteOfferingRateEditability, unknown>> = {},
) => {
  return useQuery({
    queryKey: [
      ...quoteOfferingRateServiceQueryKeys.editability(
        quoteId,
        quoteOfferingId,
        rateId,
      ),
    ],
    queryFn: async () => {
      const { data } = await apiGet<QuoteOfferingRateEditability>(
        `/api/quotes/${quoteId}/quoteOfferings/${quoteOfferingId}/rate/edit`,
      );
      return data;
    },
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

export const useEditQuoteOfferingRate = (
  options: Partial<
    UseMutationOptions<
      IQuoteRespSchema,
      unknown,
      {
        quoteId: string;
        quoteOfferingId: string;
        rate: QuoteOfferingRateEditReq;
      }
    >
  > = {},
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ quoteId, quoteOfferingId, rate }) =>
      enqueueForQuote(quoteId, async () => {
        const { data } = await apiPut<IQuoteRespSchema>(
          `/api/quotes/${quoteId}/quoteOfferings/${quoteOfferingId}/rate/edit`,
          { ...rate, overagePrices: [] },
        );
        return data;
      }),
    onSuccess: (_data, { quoteId, quoteOfferingId }) => {
      queryClient.invalidateQueries({
        queryKey: [...cpqServiceQueryKeys.quotesById(quoteId)],
      });
      queryClient.invalidateQueries({
        queryKey: [
          ...quoteOfferingRateServiceQueryKeys.editabilityBase(
            quoteId,
            quoteOfferingId,
          ),
        ],
      });
    },
    ...options,
  });
};
